import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SavedPromptCard = ({ 
  prompt, 
  onCopy, 
  onEdit, 
  onDelete, 
  isSelected, 
  onSelect 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(prompt?.content);
    onCopy(prompt?.id);
  };

  const handleDelete = () => {
    if (showDeleteConfirm) {
      onDelete(prompt?.id);
      setShowDeleteConfirm(false);
    } else {
      setShowDeleteConfirm(true);
      setTimeout(() => setShowDeleteConfirm(false), 3000);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      marketing: 'bg-blue-100 text-blue-800',
      legal: 'bg-purple-100 text-purple-800',
      technical: 'bg-green-100 text-green-800',
      creative: 'bg-orange-100 text-orange-800',
      business: 'bg-gray-100 text-gray-800'
    };
    return colors?.[category] || colors?.business;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (text?.length <= maxLength) return text;
    return text?.substring(0, maxLength) + '...';
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 transition-all duration-200 hover:shadow-md ${
      isSelected ? 'ring-2 ring-primary border-primary' : ''
    }`}>
      {/* Header with selection and category */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => onSelect(prompt?.id)}
            className="w-5 h-5 rounded border-2 border-border flex items-center justify-center hover:border-primary transition-colors"
          >
            {isSelected && (
              <Icon name="Check" size={12} color="var(--color-primary)" />
            )}
          </button>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(prompt?.category)}`}>
            {prompt?.category}
          </span>
        </div>
        <span className="text-xs text-text-secondary">
          {formatDate(prompt?.createdAt)}
        </span>
      </div>
      {/* Prompt Content */}
      <div className="mb-4">
        <p className="text-text-primary text-sm leading-relaxed">
          {isExpanded ? prompt?.content : truncateText(prompt?.content)}
        </p>
        {prompt?.content?.length > 150 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-primary text-xs font-medium mt-2 hover:underline"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
      {/* Tags */}
      {prompt?.tags && prompt?.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {prompt?.tags?.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-muted text-text-secondary text-xs rounded"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            iconName="Copy"
            iconSize={14}
          >
            Copy
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(prompt)}
            iconName="Edit"
            iconSize={14}
          >
            Edit
          </Button>
        </div>
        <Button
          variant={showDeleteConfirm ? "destructive" : "ghost"}
          size="sm"
          onClick={handleDelete}
          iconName={showDeleteConfirm ? "AlertTriangle" : "Trash2"}
          iconSize={14}
        >
          {showDeleteConfirm ? 'Confirm' : 'Delete'}
        </Button>
      </div>
    </div>
  );
};

export default SavedPromptCard;